import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { ReportUploadDto } from "@/api/dtos";
import type { ReportUploadKind, ReportUploadTarget } from "./_models/ReportUpload";

export interface CreateReportUploadArgs {
  report: ReportUploadKind;
  filename: string;
  /** yyMMdd; omitted = today in Sydney. */
  date?: string;
}

/** A presigned S3 URL to upload one report file to (POST /reports/{report}/uploads); the file goes via uploadReportFile. */
export const createReportUploadApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createReportUpload: builder.mutation<ReportUploadTarget, CreateReportUploadArgs>({
      queryFn: async (args) => {
        try {
          const res = await axios.post<ReportUploadDto>(
            `${backendBase()}/reports/${args.report}/uploads`,
            { filename: args.filename, date: args.date },
            { headers: { Accept: "application/json" } },
          );
          const target: ReportUploadTarget = {
            uploadUrl: res.data.uploadUrl,
            contentType: res.data.contentType,
            key: res.data.key,
            expiresAt: res.data.expiresAt,
          };
          return { data: target };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
    }),
  }),
});

export const createReportUpload = createReportUploadApi.endpoints.createReportUpload;
